// Shared shell for the Load Balancer sub-pages (/cluster/routing, /security).
// Owns the cluster query, the CommandBar (stats + Render & Reload) and the
// RenderDrawer so the dirty flag and apply flow stay visible no matter which
// sub-page the operator edits.
//
// It no longer reads the worker list: the pool table moved to /workers, and
// neither ConfigPanel nor TlsSecurityCard ever looked at a worker row. The
// per-worker numbers the bar used to compute from that list come from the
// cluster's own `stats`.

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@veil/ui/components/button";
import {
	Field,
	FieldError,
	FieldGroup,
	FieldLabel,
} from "@veil/ui/components/field";
import { Input } from "@veil/ui/components/input";
import { Skeleton } from "@veil/ui/components/skeleton";
import Loader2 from "lucide-react/dist/esm/icons/loader-2";
import { type ReactNode, startTransition } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { QueryError } from "@/components/query-state";
import { useClusterState } from "@/components/render-status-bar";
import { api, unwrap } from "@/lib/api";
import { hasRole, useAuth } from "@/lib/auth-context";
import { CommandBar } from "./command-bar";
import type { ClusterRow, ClusterStats } from "./types";

const PAGE_LOADING_SKELETON = (
	<div className="space-y-6">
		<Skeleton className="h-16" />
		<Skeleton className="h-72" />
	</div>
);

export interface ClusterShellContext {
	cluster: ClusterRow;
	stats: ClusterStats | undefined;
	canEdit: boolean;
	canApply: boolean;
}

export function ClusterShell({
	children,
}: {
	children: (ctx: ClusterShellContext) => ReactNode;
}) {
	const { user } = useAuth();
	const cluster = useClusterState();

	const canEdit = hasRole(user, "operator");
	const canApply = hasRole(user, "admin");
	const stats = cluster.data?.stats;
	const c = cluster.data?.cluster;

	if (cluster.isLoading) {
		return PAGE_LOADING_SKELETON;
	}

	// Must come before the !c branch: a failed request also leaves `c`
	// undefined, and rendering "create your cluster" over a 500 invites the
	// operator to re-seed a cluster that already exists.
	if (cluster.isError) {
		return (
			<QueryError
				title="Couldn't load the cluster"
				error={cluster.error}
				onRetry={() => void cluster.refetch()}
				retrying={cluster.isFetching}
			/>
		);
	}

	if (!c) {
		return <NoClusterCard canEdit={canEdit} />;
	}

	return (
		<div className="space-y-6">
			<CommandBar cluster={c} stats={stats} />

			{children({ cluster: c, stats, canEdit, canApply })}
		</div>
	);
}

// ---------------------------------------------------------------------------
// First-run empty state: prompts the operator to seed the cluster.
// ---------------------------------------------------------------------------

const NoClusterFormSchema = z.object({
	name: z.string().min(1, "Cluster name is required").max(64),
});
type NoClusterFormInput = z.infer<typeof NoClusterFormSchema>;

function NoClusterCard({ canEdit }: { canEdit: boolean }) {
	const queryClient = useQueryClient();
	const form = useForm<NoClusterFormInput>({
		resolver: zodResolver(NoClusterFormSchema),
		defaultValues: { name: "" },
	});

	const create = useMutation({
		mutationFn: (values: NoClusterFormInput) =>
			unwrap(api.api.cluster.patch({ name: values.name.trim() })),
		onSuccess: (_data, values) => {
			toast.success(`Cluster "${values.name}" created`);
			startTransition(() => {
				queryClient.invalidateQueries({ queryKey: ["cluster"] });
			});
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Failed to create"),
	});

	const onSubmit = form.handleSubmit((values) => create.mutate(values));
	const creating = create.isPending;

	return (
		<div className="mx-auto max-w-xl rounded-xl border border-border/70 bg-card p-8 shadow-sm">
			<div className="font-mono text-[0.68rem] uppercase tracking-[0.16em] text-muted-foreground">
				First run
			</div>
			<h1 className="mt-1 font-heading text-xl font-semibold tracking-tight">
				Create your cluster
			</h1>
			<p className="mt-3 text-sm leading-relaxed text-muted-foreground">
				This is a fresh cluster manager. Import from an existing production
				cluster, or pick a name below to start fresh.
			</p>
			{canEdit ? (
				<form onSubmit={onSubmit} noValidate className="mt-6">
					<FieldGroup>
						<div className="flex flex-wrap items-end gap-3">
							<Field
								className="grow"
								data-invalid={!!form.formState.errors.name}
							>
								<FieldLabel htmlFor="cluster-name">Cluster name</FieldLabel>
								<Input
									id="cluster-name"
									placeholder="e.g. dw-lb"
									aria-invalid={!!form.formState.errors.name}
									{...form.register("name")}
								/>
								<FieldError errors={[form.formState.errors.name]} />
							</Field>
							<Button type="submit" disabled={creating}>
								{creating && (
									<Loader2 data-icon="inline-start" className="animate-spin" />
								)}
								{creating ? "Creating" : "Create"}
							</Button>
						</div>
					</FieldGroup>
				</form>
			) : (
				<p className="mt-6 text-sm text-muted-foreground">
					Operator role required to create the cluster.
				</p>
			)}
		</div>
	);
}
